import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

// Staff view. The core rule: acknowledge the CHANGE for a minor revision,
// read the WHOLE policy for a major one. Viewing never counts as acknowledging.
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

// Downloads go through axios so the auth header is attached; the response is a
// blob we hand to the browser.
export async function downloadPolicy(versionId, format, onErr) {
  try {
    const r = await api.get(`/policies/versions/${versionId}/download`,
      { params: { format }, responseType: 'blob' });
    const name = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/)?.[1]
      || `policy.${format}`;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { onErr?.('Could not download the document'); }
}

function DownloadButtons({ versionId, onErr, size = 'btn-sm' }) {
  return (
    <div className="flex-gap" style={{ gap:8 }}>
      <button className={`btn btn-secondary ${size}`} onClick={() => downloadPolicy(versionId, 'pdf', onErr)}>⬇ PDF</button>
      <button className={`btn btn-secondary ${size}`} onClick={() => downloadPolicy(versionId, 'docx', onErr)}>⬇ Word</button>
    </div>
  );
}

export default function MyPolicies({ initialAck, clearArg }) {
  const [data, setData] = useState({ pending: [], done: [], superseded: [] });
  const [openAck, setOpenAck] = useState(initialAck || null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get('/policies/my').then(r => setData(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);
  // Deep link from the notification bell — open that acknowledgement directly.
  useEffect(() => { if (initialAck) setOpenAck(initialAck); }, [initialAck]);
  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  if (openAck) return <AckView ackId={openAck}
    onClose={() => { setOpenAck(null); clearArg?.(); load(); }} flash={flash} />;

  const Row = ({ p, done }) => (
    <div className="card" style={{ marginBottom:8, borderLeft: done ? '3px solid var(--green)'
        : p.overdue ? '3px solid var(--red)' : p.change_type === 'major' ? '3px solid var(--red)' : '3px solid #FF9900' }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', gap:10, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:220 }}>
          <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <span style={{ fontWeight:600 }}>{p.title}</span>
            <span className="mono" style={{ fontSize:11, color:'var(--accent)' }}>{p.policy_code}</span>
            <span style={{ fontSize:11, color:'var(--text2)' }}>v{p.version_no}</span>
            <span className="badge" style={{ fontSize:10, fontWeight:700,
              background: p.change_type === 'major' ? 'rgba(229,72,77,0.15)' : 'rgba(255,153,0,0.15)',
              color: p.change_type === 'major' ? 'var(--red)' : '#B26A00' }}>
              {p.change_type === 'major' ? 'MAJOR REVISION' : 'MINOR CHANGE'}
            </span>
          </div>
          {p.change_summary && <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:3 }}>{p.change_summary}</div>}
          <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:3 }}>
            {p.effective_date && <>Effective {fmt(p.effective_date)}</>}
            {p.due_date && <span style={{ color: p.overdue ? 'var(--red)' : 'var(--text2)' }}>
              {p.effective_date ? ' · ' : ''}Acknowledge by {fmt(p.due_date)}{p.overdue ? ' · overdue' : ''}</span>}
            {done && <> · Acknowledged {fmt(p.acknowledged_at)}</>}
          </div>
        </div>
        <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <DownloadButtons versionId={p.version_id} onErr={flash} />
          {done && <span className="badge badge-completed" style={{ fontSize:11 }}>✓ Acknowledged</span>}
          {/* Always openable — the policy is readable in the portal at any time,
              before or after acknowledging. Downloading is a choice, not the
              only way to read it. */}
          <button className={`btn btn-sm ${done ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setOpenAck(p.ack_id)}>
            {done ? 'Read policy' : p.change_type === 'major' ? 'Read full policy' : 'View what changed'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>📋 My Policies</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>
          Policies that apply to you. For a small change you only need to read what changed — you won't be asked to reread the whole policy.
        </p>
      </div>
      {msg && <div style={{ fontSize:13, marginBottom:10, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}

      <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', margin:'0 0 8px', letterSpacing:'.04em' }}>
        NEEDS YOUR ACKNOWLEDGEMENT ({data.pending.length})
      </div>
      {!data.pending.length
        ? <div className="card"><div className="empty-state"><p>Nothing waiting on you. 🎉</p></div></div>
        : data.pending.map(p => <Row key={p.ack_id} p={p} />)}

      {data.done.length > 0 && (<>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', margin:'20px 0 8px', letterSpacing:'.04em' }}>
          ACKNOWLEDGED ({data.done.length})
        </div>
        {data.done.map(p => <Row key={p.ack_id} p={p} done />)}
      </>)}
    </div>
  );
}

function AckView({ ackId, onClose, flash }) {
  const [d, setD] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get(`/policies/acks/${ackId}`).then(r => setD(r.data)).catch(()=>{}); }, [ackId]);

  // Major revisions: the acknowledge button unlocks once the end is reached.
  useEffect(() => {
    if (!d || d.required_action !== 'full' || !endRef.current) return;
    const ob = new IntersectionObserver(es => {
      if (es[0].isIntersecting) {
        setReachedEnd(true);
        api.post(`/policies/acks/${ackId}/reviewed`).catch(()=>{});
      }
    }, { threshold: 0.5 });
    ob.observe(endRef.current);
    return () => ob.disconnect();
  }, [d, ackId]);

  const ack = async () => {
    setBusy(true);
    try { await api.post(`/policies/acks/${ackId}/acknowledge`); flash('✓ Acknowledged — thank you'); onClose(); }
    catch (e) { flash(e.response?.data?.error || 'Could not record your acknowledgement'); }
    finally { setBusy(false); }
  };

  if (!d) return null;
  const major = d.required_action === 'full';
  const canAck = !major || reachedEnd;
  const alreadyDone = d.status === 'acknowledged';

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ marginBottom:12 }}>◀ My policies</button>

      <div className="card" style={{ marginBottom:14, borderLeft:`3px solid ${major ? 'var(--red)' : '#FF9900'}` }}>
        <div className="flex-gap" style={{ gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontSize:19, fontWeight:700 }}>{d.title}</span>
          <span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{d.policy_code}</span>
          <span style={{ fontSize:12, color:'var(--text2)' }}>v{d.version_no}</span>
        </div>
        <div style={{ fontSize:13, marginTop:6, fontWeight:600,
          color: alreadyDone ? 'var(--green)' : major ? 'var(--red)' : '#B26A00' }}>
          {alreadyDone
            ? 'You have acknowledged this version. The current policy is below.'
            : major
              ? 'This policy has had a major revision — please read it in full before acknowledging.'
              : 'This is a small change. What changed is highlighted below, and the full policy follows.'}
        </div>
        {/* What the policy is about. Stored on the policy itself, so it stays
            the same across versions — the change summary below says what moved
            in THIS version. */}
        {d.description && <div style={{ fontSize:13, marginTop:6, color:'var(--text2)' }}>{d.description}</div>}
        {d.change_summary && <div style={{ fontSize:13, marginTop:6 }}><b>Summary:</b> {d.change_summary}</div>}
        {d.effective_date && <div style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>Effective {fmt(d.effective_date)}</div>}
        <div style={{ marginTop:10 }}><DownloadButtons versionId={d.version_id} onErr={flash} /></div>
      </div>

      {/* What changed — shown for both, but it's the whole story for a minor change */}
      {d.changes.length > 0 && (
        <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', padding:'10px 12px', letterSpacing:'.04em' }}>WHAT CHANGED</div>
          <div style={{ padding:'0 14px 12px' }}>
            {d.changes.map(c => (
              <div key={c.id} style={{ borderTop:'1px solid var(--border)', padding:'10px 0' }}>
                <div className="flex-gap" style={{ gap:8, alignItems:'center', marginBottom:6 }}>
                  <b style={{ fontSize:13.5 }}>{c.heading}</b>
                  <span className="badge badge-normal" style={{ fontSize:10 }}>{c.change_kind}</span>
                </div>
                {c.old_text && (
                  <div style={{ fontSize:12.5, padding:'6px 10px', borderRadius:6, background:'rgba(229,72,77,0.08)', marginBottom:6 }}>
                    <span style={{ fontSize:10.5, color:'var(--red)', fontWeight:600 }}>PREVIOUS</span>
                    <div style={{ whiteSpace:'pre-wrap', color:'var(--text2)' }}>{c.old_text}</div>
                  </div>
                )}
                {c.new_text && (
                  <div style={{ fontSize:12.5, padding:'6px 10px', borderRadius:6, background:'rgba(45,168,102,0.10)' }}>
                    <span style={{ fontSize:10.5, color:'var(--green)', fontWeight:600 }}>NOW</span>
                    <div style={{ whiteSpace:'pre-wrap' }}>{c.new_text}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The policy itself, always readable in the portal. Downloading is a
          convenience, never the only way to read it. */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', letterSpacing:'.04em' }}>FULL POLICY</div>
          <span style={{ fontSize:11.5, color:'var(--text2)' }}>{d.sections.length} section(s)</span>
        </div>
        {!d.sections.length && <div style={{ fontSize:13, color:'var(--text2)' }}>This version has no content.</div>}
        {d.sections.map((s, i) => (
          <div key={s.id} style={{ marginBottom:14 }}>
            <b style={{ fontSize:14 }}>{i + 1}. {s.heading}</b>
            <div style={{ fontSize:13.5, whiteSpace:'pre-wrap', color:'var(--text2)', marginTop:4 }}>{s.body_html}</div>
          </div>
        ))}
        <div ref={endRef} style={{ height:1 }} />
      </div>

      {alreadyDone ? (
        <div className="card" style={{ position:'sticky', bottom:12 }}>
          <span className="badge badge-completed" style={{ fontSize:12 }}>
            ✓ You acknowledged this on {fmt(d.acknowledged_at)}
          </span>
        </div>
      ) : (
        <div className="card" style={{ position:'sticky', bottom:12 }}>
          {major && !reachedEnd && (
            <div style={{ fontSize:12.5, color:'var(--text2)', marginBottom:8 }}>
              Scroll to the end of the policy to enable the acknowledgement.
            </div>
          )}
          <button className="btn btn-primary" disabled={!canAck || busy} onClick={ack}>
            {busy ? 'Recording…' : major
              ? 'I have read and understood this policy'
              : 'I have read and understood this change'}
          </button>
        </div>
      )}
    </div>
  );
}
